import type React from "react";
import { useContext, useEffect, useState } from "react";

import { faShuffle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { Treatment } from "api/types/Treatment";
import { DropdownIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { Tag } from "shared/atoms/Tag";
import { theme } from "theme";
import { CurrentPatientContext } from "utils/contexts";
import { userIsPartner } from "utils/profile/profileHelper";
import { translateTreatment } from "utils/translations";

import { OptionItem, StyledTreatmentDropDownButton } from "./components/PatientHeaderDropdownStyles";
import { TreatmentSwitch } from "./PatientDataList/TreatmentSwitch";

const getCurrentTreatment = (treatments: Treatment[]): Treatment => {
  return treatments.find(treatment => treatment.is_current_treatment) as Treatment;
};

export const TreatmentDropdown: React.FC = () => {
  const { patient, previousTreatmentId, setPreviousTreatmentId } = useContext(CurrentPatientContext);
  const treatments = patient.treatments as Treatment[];
  const previousTreatment = treatments.find((tm: Treatment) => tm.id === previousTreatmentId);
  const [treatmentOptions, setTreatmentOptions] = useState<Treatment[]>(treatments);
  const [selectedOption, setSelectedOption] = useState<Treatment | null>(
    previousTreatment || getCurrentTreatment(treatments)
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useTranslation();
  const [showTreatmentSwitch, setShowTreatmentSwitch] = useState(false);
  const { profile } = useProfileContext();
  const isPartner = userIsPartner(profile);

  useEffect(() => {
    const sortedArray = patient.treatments
      .filter((treatment: Treatment) => treatment.id !== selectedOption?.id)
      .sort((a: Treatment, b: Treatment) => {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });
    setTreatmentOptions(sortedArray);

    if (previousTreatment) {
      setSelectedOption(previousTreatment);
    } else {
      setSelectedOption(getCurrentTreatment(treatments));
    }
  }, [patient]);

  useEffect(() => {
    if (selectedOption?.id) {
      if (!selectedOption.is_current_treatment) {
        setPreviousTreatmentId(selectedOption.id);
      } else {
        setPreviousTreatmentId(null);
      }
    }
  }, [selectedOption]);

  const hasPreviousTreatments = treatments.length > 1;

  const treatmentMenuItems = treatmentOptions.map(option => ({
    children: (
      <OptionItem key={JSON.stringify(option)}>
        <TreatmentOption option={option} showTag={hasPreviousTreatments} />
      </OptionItem>
    ),
    onClick: () => {
      setSelectedOption(option);
      setMenuOpen(false);
    },
  }));

  const switchTreatmentMenuItem = {
    children: (
      <SwitchTreatmentOptionItem key="switch-treatment-option-item">
        <IconContainer>
          <FontAwesomeIcon icon={faShuffle} color={theme.colors.white} style={{ height: "12px", width: "12px" }} />
        </IconContainer>
        {t("switch_treatment.label")}
      </SwitchTreatmentOptionItem>
    ),
    onClick: () => {
      setShowTreatmentSwitch(true);
    },
  };

  const menuOptions = isPartner ? treatmentMenuItems : [...treatmentMenuItems, switchTreatmentMenuItem];
  const isDropdownDisabled = isPartner && menuOptions.length === 0;

  return (
    <>
      <Label>{t("patients.header_info.treatment.header")}</Label>
      <Wrapper>
        <StyledTreatmentDropDownButton
          id="TreatmentDropdown"
          matchAnchorWidth
          options={menuOptions}
          disabled={isDropdownDisabled}
          onClick={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
          dropdownListMinWidth="298px"
          dropdownOrigin="right"
          $isOpen={menuOpen}
        >
          {selectedOption && (
            <OptionItemSelected>
              <TreatmentOption option={selectedOption} showTag={hasPreviousTreatments} />
            </OptionItemSelected>
          )}
          {!isDropdownDisabled && <DownChevron $isOpen={menuOpen} />}
        </StyledTreatmentDropDownButton>
      </Wrapper>
      {showTreatmentSwitch && <TreatmentSwitch onClose={() => setShowTreatmentSwitch(false)} />}
    </>
  );
};

const TreatmentOption = ({ option, showTag }: { option: Treatment; showTag: boolean }) => {
  const { t } = useTranslation();

  const { ailment, lateral_location, pain_location } = option;

  return (
    <>
      {showTag && (
        <TreatmentTag
          bgColor={
            option.is_current_treatment
              ? theme.colors.treatmentSwitching.current
              : theme.colors.treatmentSwitching.previous
          }
          color={theme.colors.primary.base}
          borderColor="transparent"
          label={
            option.is_current_treatment
              ? t("patients.header_info.treatment.current")
              : t("patients.header_info.treatment.previous")
          }
        />
      )}
      {translateTreatment({ ailment, lateral_location, location: pain_location })}
    </>
  );
};

const OptionItemSelected = styled.div`
  padding: 8px 0;
  display: flex;
  flex-grow: 1;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.redesign.db100};
`;

const TreatmentTag = styled(Tag)`
  margin-right: ${props => props.theme.spacing.S_10};
  border: none;
  height: 24px;
  line-height: 24px;
`;

const DownChevron = styled(DropdownIcon)<{ $isOpen: boolean }>`
  transform: ${({ $isOpen }) => $isOpen && "rotate(180deg)"};
  transition: 0.3s;
  color: ${props => props.theme.colors.redesign.db100};
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  margin: 16px 0;
  background: ${props => props.theme.semantic.container.background};
  border: 1px solid ${props => props.theme.colors.redesign.db20};
  border-radius: 8px;
  transition: 0.2s;

  &:hover {
    background: ${props => props.theme.colors.redesign.b10};

    ${OptionItemSelected} {
      color: ${props => props.theme.colors.redesign.b100};
    }
    ${DownChevron} path {
      stroke: ${props => props.theme.colors.redesign.b100};
    }
  }
`;

const Label = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.redesign.db100};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.redesign.b100};
  border-radius: 4px;
  margin-right: 8px;
  padding: 4px 8px;
`;

const SwitchTreatmentOptionItem = styled(OptionItem)`
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.theme.colors.redesign.db100};

  &:hover ${IconContainer} {
    background: ${props => props.theme.colors.white};
  }

  &:hover ${IconContainer} svg path {
    fill: ${props => props.theme.colors.redesign.db80};
  }
`;
